import { Sheet, useSheet } from "@/domain/Sheet";
import {
  SheetDataRow,
  SheetDataRowQueryPrefix,
  useSheetDataRowUtils,
  useSheetDataRowsQuery,
} from "@/domain/SheetDataRow";
import { SheetFieldSchema } from "@/domain/SheetFieldSchema";
import { getSinglyLinkedSheetDataRowList } from "@/helpers/getSinglyLinkedSheetDataRowList";
import { memo, useEffect, useMemo } from "react";
import React from "react";
import {
  MinimalTypeSchemasToRawSheetRowData,
  SheetRowsIO,
  SheetRowsIOTransactionJob,
} from "./core";
import { SheetRowsIOQuery } from "./query";

type firestoreSheetIdentifiers = {
  // シートを指定するための要素
  organizationId: string;
  versionId: string;
  sheetId: string;
};

const transformValuesToSheetDataCells = (
  value: Record<string, string>,
  sheetFieldSchemas: SheetFieldSchema[]
) => {
  const sheetDataCells: Record<string, string> = {};
  for (const key in value) {
    // schemaのnameが重複した際はsort順が若いものを優先する（NOTE: name重複が禁止されたら本ならびかえは不要）
    const sortedSheetFieldSchemas = [...sheetFieldSchemas].sort((a, b) => {
      return Math.sign(a.sort - b.sort);
    });

    const matchingSchemas = sortedSheetFieldSchemas.filter(
      (s) => s.name === key
    );
    // 現状、schemaのnameの重複をシート画面上では許しているため、チェックを入れる（NOTE: name重複が禁止されたら不要）
    if (matchingSchemas.length > 1) {
      console.warn(`Multiple names founded in schema: ${key}`);
    }
    const schema = matchingSchemas[0];
    sheetDataCells[schema.id] = value[key];
  }
  return sheetDataCells;
};

export const getSortedRowsFromLinkedList = (
  sheet: Sheet,
  rows: SheetDataRow[]
): { rows: SheetDataRow[] | null; rowIndexes: { [key: string]: number } } => {
  const rowsMap: { [id: string]: SheetDataRow } = Object.create(null);
  const rowIndexes: { [key: string]: number } = Object.create(null);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    rowsMap[row.id] = row;
  }

  const sortedRows: SheetDataRow[] = [];
  let nextId = sheet.firstRowId;
  while (true) {
    if (!nextId) break;
    const current: SheetDataRow | undefined = rowsMap[nextId];
    if (!current) {
      return { rows: null, rowIndexes };
    }
    rowIndexes[current.id] = sortedRows.length;
    sortedRows.push(current);
    nextId = current.nextRowId;
  }

  return {
    rows: sortedRows.length === rows.length ? sortedRows : null,
    rowIndexes,
  };
};

const generateRowIndexesFromRows = (rows: SheetDataRow[]) => {
  let i = 0;
  return rows.reduce(
    (acc, row) => {
      acc[row.id] = i++;
      return acc;
    },
    {} as { [key: string]: number }
  );
};

export const SheetRowsIOFirestoreHost = memo(
  ({
    sheetFieldSchemas,
    organizationId,
    versionId,
    sheetId,
    queries,
    setSheetRowsIOUnique,
    setRawRowsState,
  }: {
    sheetFieldSchemas: SheetFieldSchema[];
    organizationId: string;
    versionId: string;
    sheetId: string;
    queries: SheetRowsIOQuery[] | null;
    setSheetRowsIOUnique: (
      value: Pick<SheetRowsIO<unknown>, "runTransaction" | "getRowById"> | null
    ) => void;
    setRawRowsState: React.Dispatch<
      React.SetStateAction<{
        rawRows: {
          id: string;
          value: MinimalTypeSchemasToRawSheetRowData<unknown>;
        }[];
        rowIndexes: {
          [key: string]: number;
        };
        isRowsLoaded: boolean;
      }>
    >;
  }) => {
    const sheetIdentifiers: firestoreSheetIdentifiers = useMemo(
      () => ({
        organizationId,
        versionId,
        sheetId,
      }),
      [organizationId, versionId, sheetId]
    );

    const [sheet] = useSheet(sheetId, { versionId, organizationId });
    const [sheetRows, isSheetRowsLoaded] = useSheetDataRowsQuery(
      {
        ...sheetIdentifiers,
      },
      async () => {
        return queries?.length
          ? {
              logicalOp: "and",
              ops: queries.map(({ sheetFieldSchemaId, operator, value }) => ({
                fieldPath: `${SheetDataRowQueryPrefix}.${sheetFieldSchemaId}`,
                opStr: operator,
                value: value,
              })),
            }
          : null;
      }
    );
    const setSheetDataRowUtils = useSheetDataRowUtils();

    const rowSchemaFitCache = useMemo(
      () => new WeakMap<SheetDataRow, any>(),
      [sheetFieldSchemas]
    );
    useEffect(() => {
      //Note:sheetRowsをlistenして単方向リスト並び替えて{[K in rowSchemas[number]["name"]]: string}でマッピングしてsetRawRowsState

      if (!sheet || !isSheetRowsLoaded || !sheetFieldSchemas?.length) return;

      const { rows: sortedRows, rowIndexes } = queries?.length
        ? { rows: sheetRows, rowIndexes: generateRowIndexesFromRows(sheetRows) }
        : getSortedRowsFromLinkedList(sheet, sheetRows);
      if (!sortedRows) return;
      // 現状、schemaのnameの重複をシート画面上では許しているため、チェックを入れる（NOTE: name重複が禁止されたら不要）
      const uniqueSheetFieldSchemas = sheetFieldSchemas.filter(
        (schema, index, self) => {
          const isUnique =
            index === self.findIndex((s) => s.name === schema.name);
          if (!isUnique) {
            console.warn(`Multiple names founded in schema: ${schema.name}`);
          }
          return isUnique;
        }
      );
      const rawRows = sortedRows.map((row) => {
        let cached = rowSchemaFitCache.get(row);
        if (cached) return cached;
        const acc = {} as { [key: string]: string };
        for (const schema of sheetFieldSchemas) {
          acc[schema.name] = row.sheetDataCells[schema.id] ?? "";
        }
        cached = { id: row.id, value: acc };
        rowSchemaFitCache.set(row, cached);
        return cached;
      });

      setRawRowsState({ rawRows, rowIndexes, isRowsLoaded: true });
    }, [sheetFieldSchemas, sheetIdentifiers, sheet, sheetRows]);

    useEffect(() => {
      const runTransaction = async (
        jobs: SheetRowsIOTransactionJob<unknown>[]
      ) => {
        for (const job of jobs) {
          if (job.type === "insert") {
            await setSheetDataRowUtils.addRowsByData(
              job.rows.map((r) => ({
                id: r.id,
                sheetDataCells: transformValuesToSheetDataCells(
                  r.value,
                  sheetFieldSchemas
                ),
              })),
              { ...sheetIdentifiers },
              {
                ...(job.insertAfterRowId
                  ? { insertAfterRowId: job.insertAfterRowId }
                  : { insertAtTop: true }),
              }
            );
          } else if (job.type === "update") {
            await setSheetDataRowUtils.updateRows(
              job.rows.map((r: any) => ({
                id: r.id,
                sheetDataCells: transformValuesToSheetDataCells(
                  r.value,
                  sheetFieldSchemas
                ),
              })),
              { ...sheetIdentifiers }
            );
          } else if (job.type === "move") {
            if (job.moveAfterRowId) {
              await setSheetDataRowUtils.moveRowsAfterTargetRow(
                job.rowIds,
                job.moveAfterRowId,
                { ...sheetIdentifiers }
              );
            } else {
              await setSheetDataRowUtils.moveRowsToTop(job.rowIds, {
                ...sheetIdentifiers,
              });
            }
          } else if (job.type === "delete") {
            await setSheetDataRowUtils.deleteRowsByIds(job.rowIds, {
              ...sheetIdentifiers,
            });
          } else {
            throw new Error("invalid job type");
          }
        }
      };

      setSheetRowsIOUnique({ runTransaction, getRowById: () => null });

      return () => {
        setRawRowsState({ rawRows: [], rowIndexes: {}, isRowsLoaded: false });
        setSheetRowsIOUnique(null);
      };
    }, [sheetFieldSchemas, sheetIdentifiers, queries]);
    return null;
  }
);
